import { Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import DocumentIcon from "src/core/assets/images/icons/document.svg?react";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { Button } from "src/core/components/ui/button";
import { Checkbox } from "src/core/components/ui/checkbox";
import { MappedStakeholder } from "src/core/constants/folder";
import { DocumentStatus } from "src/core/gql/graphql";
import { cn } from "src/core/lib/utils";

type DocumentsColumnProps = {
  row: Row<MappedStakeholder>;
  isReadOnly: boolean;
  fileLoading: string[];
  handleDocument: (code: string, stakeholder: MappedStakeholder, label: string) => void;
  handlePreviewDocument: (document: { id: string; name: string }) => void;
  handleExternalDocumentChange: (
    checked: boolean,
    code: string,
    stakeholderId: string,
    documentId: string | null,
    inputId: string
  ) => void;
};
export const DocumentsColumn = ({
  row,
  isReadOnly,
  fileLoading,
  handleDocument,
  handlePreviewDocument,
  handleExternalDocumentChange,
}: DocumentsColumnProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-y-5">
      {row.original?.documents?.map((document) => {
        const inputId = `${row.index}-${document?.type?.code}`;
        const externalDocumentInputId = `documents.${inputId}.existing`;
        const isDisabled =
          isReadOnly ||
          document.uploaded ||
          fileLoading.includes(inputId) ||
          fileLoading.includes(externalDocumentInputId);

        return (
          <div key={inputId} className="flex flex-row items-center gap-x-10 px-5">
            <>
              {document.uploaded || document.externalDocument ? (
                <div
                  role="button"
                  onClick={() => {
                    if (document.id && !document.externalDocument)
                      handlePreviewDocument({ id: document.id, name: document.fileName ?? "" });
                  }}
                  className={cn(
                    "stakeholders-datatable-document-card charged",
                    document.externalDocument && "hover:cursor-default"
                  )}
                >
                  <span className="line-clamp-2">{document?.type?.name}</span>
                </div>
              ) : (
                <div className="flex flex-col items-start gap-y-5">
                  <Button
                    className="stakeholders-datatable-document-card stakeholders-datatable-document-card-hover group"
                    variant="upload"
                    size="none"
                    onClick={() => {
                      document?.type?.code &&
                        document?.type?.name &&
                        handleDocument(document?.type?.code, row.original, document?.type?.name);
                    }}
                    loading={fileLoading.includes(inputId)}
                    disabled={isDisabled}
                  >
                    <div className="flex flex-row items-center gap-x-10 w-[160px] hover:cursor-pointer">
                      <DocumentIcon className="text-muted-foreground flex-shrink-0 group-hover:text-blue-completed" />
                      <div className="line-clamp-2 min-w-0">{document?.type?.name}</div>
                    </div>
                  </Button>
                </div>
              )}
              {document.uploaded && !document.externalDocument ? (
                document.status === DocumentStatus.Expired ? (
                  <div className="font-light text-alert">
                    {t("stakeholders.datatable.documents.expired")}
                  </div>
                ) : (
                  <div className="font-light text-muted-foreground">
                    {t("stakeholders.datatable.documents.charged")}
                  </div>
                )
              ) : fileLoading.includes(externalDocumentInputId) ? (
                <Spinner />
              ) : (
                <div className="flex flex-row items-center gap-x-5">
                  <Checkbox
                    id={externalDocumentInputId}
                    className="text-blue-primary"
                    onCheckedChange={(checked) => {
                      if (document?.type?.code) {
                        handleExternalDocumentChange(
                          Boolean(checked),
                          document?.type?.code,
                          row.original?.id,
                          document.id ?? null,
                          externalDocumentInputId
                        );
                      }
                    }}
                    checked={Boolean(document.externalDocument)}
                    disabled={isReadOnly || fileLoading.includes(inputId)}
                  />
                  <label htmlFor={externalDocumentInputId} className="font-light">
                    {t("stakeholders.datatable.documents.GED")}
                  </label>
                </div>
              )}
            </>
          </div>
        );
      })}
    </div>
  );
};
